import axios from "axios";
import { getApiBaseUrl } from "../utils/apiConfig";
import { refreshSocketAuth } from "./socket.service";

const getBaseURL = () => {
  const baseURL = getApiBaseUrl();
  if (process.env.NODE_ENV === "development") {
    console.log("API Base URL:", baseURL);
    console.log("Hostname:", typeof window !== "undefined" ? window.location.hostname : "N/A");
  }
  return baseURL;
};

const api = axios.create({
  baseURL: getBaseURL(),
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = [];
let securityAlertHandler: (() => void) | null = null;

/** Register the callback that shows the security alert dialog. Call this once from the app root. */
export function setSecurityAlertHandler(handler: () => void) {
  securityAlertHandler = handler;
}

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
};

const triggerSecurityAlert = () => {
  localStorage.removeItem("token");
  if (securityAlertHandler) {
    securityAlertHandler();
  } else {
    window.location.href = "/login";
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Reuse detected on the original request — show security alert immediately
      if (error.response?.data?.data?.code === "TOKEN_REUSE_DETECTED") {
        triggerSecurityAlert();
        return Promise.reject(error);
      }

      // Another refresh is already in flight — queue this request
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Use a plain axios call (NOT the api instance) so the request interceptor
        // does not attach the expired access token in the Authorization header.
        // The httpOnly refresh-token cookie is sent automatically via withCredentials.
        const refreshResponse = await axios.post(
          `${getBaseURL()}/admins/refresh-token`,
          {},
          { withCredentials: true }
        );
        const { accessToken } = refreshResponse.data.data;

        localStorage.setItem("token", accessToken);
        api.defaults.headers.common["Authorization"] = "Bearer " + accessToken;
        // Re-authenticate the socket so its next reconnect handshake uses the fresh token.
        refreshSocketAuth();
        processQueue(null, accessToken);

        originalRequest.headers["Authorization"] = "Bearer " + accessToken;
        return api(originalRequest);
      } catch (refreshErr: unknown) {
        const errResponse = (refreshErr as { response?: { status?: number; data?: { data?: { code?: string } } } }).response;
        const code = errResponse?.data?.data?.code;

        if (code === "TOKEN_REUSE_DETECTED") {
          processQueue(refreshErr, null);
          triggerSecurityAlert();
          return Promise.reject(refreshErr);
        }

        // Transient error (network timeout, 5xx) — do not sign the user out.
        // The current batch of requests will fail; the next user action can retry.
        const isTransient = !errResponse || (errResponse.status !== undefined && errResponse.status >= 500);
        if (isTransient) {
          processQueue(refreshErr, null);
          return Promise.reject(refreshErr);
        }

        // Real auth failure (expired or revoked refresh token) — force sign-out
        processQueue(refreshErr, null);
        localStorage.removeItem("token");
        window.location.href = "/login";
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
